// src/utils/approvalUtils.js

export const deriveApprovalStatus = (approverList = []) => {
  const list = Array.isArray(approverList) ? approverList : [];
  
  // 1. If no approvers were selected during creation -> NA
  if (list.length === 0) return "NA";

  // Sort by level to process sequentially
  const sorted = [...list].sort((a, b) => Number(a.level) - Number(b.level));

  // 2. Check for Returned / Rejected / Correction first
  const returnedOrRejected = sorted.find((r) => {
    const s = String(r?.status || "").trim().toLowerCase();
    return s.includes("rejected") || s.includes("return") || s.includes("correction");
  });
  if (returnedOrRejected) return "Returned";

  // 3. Iterate through levels sequentially
  for (let i = 0; i < sorted.length; i++) {
    const currentLevel = sorted[i];
    const rawStatus = String(currentLevel?.status || "").trim();
    const statusLower = rawStatus.toLowerCase();
    
    // A level is considered "Approved" if its status starts with "approved"
    // OR if it is the final level and the status is not pending
    const isFinalLevel = i === sorted.length - 1;
    const isApproved = statusLower.startsWith("approved") || (isFinalLevel && !statusLower.includes("pending"));

    if (!isApproved) {
      // If the status contains "pending", show "Draft"
      if (statusLower.includes("pending")) {
        return "Draft";
      }
      
      // If the status is exactly "Approved Level 1" or similar, 
      // it means the SVL status wasn't picked up. We strip "Level X" to find the custom SVL status.
      // If it literally says "Approved Level 1", we return "Approved Level 1" (or you can map it to your SVL).
      // Otherwise, return the exact custom status (e.g., "Reviewed").
      return rawStatus;
    }
  }

  // 4. If all levels are completed and the final level was approved
  return "Approved";
};

/** True when the asset can be edited/deleted (Draft, Returned, or NA). */
export const isEditableByApproval = (asset) => {
  const status = String(deriveApprovalStatus(asset?.approverList)).toLowerCase();
  return status === "draft" || status === "returned" || status === "na";
};

/** True when the asset is active enough for scan / assign / stock / dispose. */
export const isScanAllowedByApproval = (asset) => {
  const list = Array.isArray(asset?.approverList) ? asset.approverList : [];
  if (list.length === 0) return true;            // legacy / workflow off
  const status = String(deriveApprovalStatus(list)).toLowerCase();
  return status === "approved" || status === "na";
};

/** True when the asset is mid-approval. */
export const isUnderApproval = (asset) => {
  const list = Array.isArray(asset?.approverList) ? asset.approverList : [];
  if (list.length === 0) return false;
  const status = String(deriveApprovalStatus(list)).toLowerCase();
  return status === "draft" || status === "pending" || status === "returned";
};

/**
 * Returns an array of emails for users at this level who have NOT yet approved.
 */
export const getPendingApproverEmails = (row) => {
  if (!row) return [];

  const s = String(row.status || "").toLowerCase();
  if (s.includes("return") || s.includes("rejected") || s.includes("correction")) return [];
  if (s.startsWith("approved")) return [];

  const users = Array.isArray(row.users) && row.users.length > 0
    ? row.users.map((e) => String(e).trim().toLowerCase())
    : (row.email ? [String(row.email).trim().toLowerCase()] : []);

  const approvedUsers = Array.isArray(row.approvedUsers)
    ? row.approvedUsers.map(e => String(e).trim().toLowerCase())
    : [];

  return users.filter(email => !approvedUsers.includes(email));
};

/**
 * Returns the single lowest-level row that still has a pending status.
 */
export const getActivePendingRow = (asset) => {
  const list = Array.isArray(asset?.approverList) ? asset.approverList : [];

  const hasReturnOrReject = list.some((r) => {
    const s = String(r?.status || "").toLowerCase();
    return s.includes("return") || s.includes("rejected") || s.includes("correction");
  });
  if (hasReturnOrReject) return null;

  return [...list]
    .sort((a, b) => Number(a.level) - Number(b.level))
    .find((r) => String(r?.status || "").toLowerCase().includes("pending")) || null;
};